import { useMemo, useState } from "react";
import { Tag } from "lucide-react";
import { Panel, Result, Cta, Segmented, ApplySize, useTool, field, label, fmt } from "./_ui";

const STANDARD = [
  [2, 1], [2, 2], [2.5, 2.5], [3, 2], [3, 3], [4, 2], [4, 3], [4, 4], [4, 6], [5, 3], [6, 2], [6, 3], [6, 4], [8, 2], [8, 3], [8.5, 2.75], [9, 3], [10, 3], [12, 4],
];
const CONTAINERS = [
  { label: "4 oz jar (2.4\" Ø)", d: 2.4, h: 1.75 },
  { label: "8 oz mason jar (2.75\" Ø)", d: 2.75, h: 2.25 },
  { label: "16 oz mason jar (3.25\" Ø)", d: 3.25, h: 3.5 },
  { label: "2 oz dropper bottle (1.25\" Ø)", d: 1.25, h: 2 },
  { label: "8 oz Boston round (2.1\" Ø)", d: 2.1, h: 3.25 },
  { label: "12 oz beer/soda can (2.6\" Ø)", d: 2.6, h: 3.5 },
  { label: "750 ml wine bottle (3\" Ø)", d: 3.0, h: 4 },
  { label: "8 oz candle tin (3\" Ø)", d: 3.0, h: 1.75 },
  { label: "16 oz coffee bag (flat face)", d: null, w: 5, h: 8 },
];

export function LabelSizeFinder() {
  const { onApplySize } = useTool();
  const [shape, setShape] = useState("round");
  const [d, setD] = useState(2.75);
  const [flatW, setFlatW] = useState(5);
  const [hgt, setHgt] = useState(2.25);
  const [style, setStyle] = useState("partial");

  const r = useMemo(() => {
    const height = Math.max(0.5, hgt - 0.5);
    let width;
    if (shape === "round") {
      const circ = Math.PI * d;
      width = style === "full" ? circ + 0.25 : style === "half" ? circ / 2 : Math.max(1, circ - 0.5);
    } else {
      width = Math.max(0.75, flatW - 0.5);
    }
    const area = width * height;
    const nearest = [...STANDARD].sort((a, b) => Math.abs(a[0] * a[1] - area) + Math.abs(a[0] - width) - (Math.abs(b[0] * b[1] - area) + Math.abs(b[0] - width))).slice(0, 3);
    return { width, height, nearest, circ: shape === "round" ? Math.PI * d : null };
  }, [shape, d, flatW, hgt, style]);

  const applyContainer = (e) => {
    const c = CONTAINERS[+e.target.value]; if (!c) return;
    if (c.d) { setShape("round"); setD(c.d); } else { setShape("flat"); setFlatW(c.w); }
    setHgt(c.h);
  };

  return (
    <Panel icon={Tag} title="Label size finder" testId="label-size-finder">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="col-span-2"><label className={label}>Common container</label>
          <select className={field} onChange={applyContainer} defaultValue="" data-testid="label-container"><option value="">Pick a container… or measure yours</option>{CONTAINERS.map((c, i) => <option key={c.label} value={i}>{c.label}</option>)}</select></div>
        <div><label className={label}>Shape</label><Segmented value={shape} onChange={setShape} options={[{ value: "round", label: "Round" }, { value: "flat", label: "Flat / box" }]} testId="label-shape" /></div>
        {shape === "round"
          ? <div><label className={label}>Diameter (in)</label><input type="number" step="0.05" min="0.5" value={d} onChange={(e) => setD(+e.target.value)} className={field} data-testid="label-diameter" /></div>
          : <div><label className={label}>Face width (in)</label><input type="number" step="0.1" min="0.5" value={flatW} onChange={(e) => setFlatW(+e.target.value)} className={field} data-testid="label-flat-width" /></div>}
        <div><label className={label}>Straight-wall height (in)</label><input type="number" step="0.05" min="0.5" value={hgt} onChange={(e) => setHgt(+e.target.value)} className={field} data-testid="label-height" /></div>
      </div>
      {shape === "round" && (
        <div className="mt-3"><label className={label}>Wrap style</label>
          <Segmented value={style} onChange={setStyle} options={[{ value: "partial", label: "Front panel (gap at back)" }, { value: "half", label: "Half wrap" }, { value: "full", label: "Full wrap + ¼\" overlap" }]} testId="label-style" /></div>
      )}
      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <Result title="Recommended label size" value={`${fmt(r.width)}" × ${fmt(r.height)}"`} sub={"width × height — leaves ¼\" clear above and below the straight wall"} testId="label-result-size" />
        <Result title={shape === "round" ? "Circumference" : "Face width"} value={shape === "round" ? `${fmt(r.circ)}"` : `${fmt(flatW)}"`} sub={shape === "round" ? "π × diameter — a label wider than this needs overlap" : "label kept ¼\" inside each edge"} testId="label-result-circ" />
        <Result title="Nearest stock sizes" value={r.nearest.map(([a, b]) => `${a}×${b}`).join(" · ")} sub="standard dies — no custom die charge" testId="label-result-standard" />
      </div>
      <p className="text-xs text-white/70 mt-4">Tapered containers: measure the diameter at the top and bottom of the label area; if they differ by more than 1/8", use a shorter label or ask us about a contour (curved) die. Squeeze bottles and pouches: keep the label 3/8" from any seam or curve.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <ApplySize w={r.width} h={r.height} productSlug="paper-roll-labels" testId="label-apply-size">Use this size{onApplySize ? "" : " — paper labels"}</ApplySize>
        {!onApplySize && <ApplySize w={r.width} h={r.height} productSlug="white-bopp-labels" testId="label-apply-size-bopp">Use this size — waterproof BOPP</ApplySize>}
        <Cta to="/blog/recycled-kraft-roll-labels-eco-friendly" outline testId="label-cta-kraft">Recycled kraft labels</Cta>
        <Cta to={`/quote?product=${encodeURIComponent(`Roll labels ${fmt(r.width)} x ${fmt(r.height)} in`)}`} outline testId="label-cta-quote">Quote this size</Cta>
      </div>
    </Panel>
  );
}
